// src/components/SearchForm.jsx
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function SearchForm({
    content,
    userId,
    specifyDay,
    date,
    searchByUsername,
    setContent,
    setUserId,
    setSpecifyDay,
    setDate,
    setSearchByUsername,
    onSearch,
}) {
    const [isExpanded, setIsExpanded] = useState(true);

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            onSearch();
        }
    };

    const handleUserInputChange = (event) => {
        const value = event.target.value;
        if (searchByUsername || /^\d*$/.test(value)) {
            setUserId(value);
        }
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="absolute -top-4 right-0 grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-indigo-600 dark:hover:bg-slate-700 dark:hover:text-indigo-400"
                aria-label={isExpanded ? "Collapse search form" : "Expand search form"}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            <div className={`overflow-hidden pr-11 transition-all duration-300 ${
                isExpanded ? "max-h-[620px] opacity-100" : "max-h-0 opacity-0"
            }`}>
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(260px,0.75fr)_auto] xl:items-end">
                    <div className="space-y-2">
                        <label htmlFor="content" className="block text-sm font-bold text-slate-700 dark:text-slate-200">
                            Message content
                        </label>
                        <div className="relative">
                            <input
                                id="content"
                                type="text"
                                placeholder="Search messages"
                                value={content}
                                onChange={(event) => setContent(event.target.value)}
                                className="h-11 w-full rounded-lg border border-slate-300 bg-slate-50 px-4 pr-28 text-slate-900 placeholder-slate-400 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/20"
                                onKeyDown={handleKeyPress}
                            />
                            <button
                                type="button"
                                onClick={onSearch}
                                className="absolute right-1.5 top-1.5 flex h-8 items-center gap-1.5 rounded-md bg-indigo-500 px-3 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                </svg>
                                Search
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="userId" className="block text-sm font-bold text-slate-700 dark:text-slate-200">
                            User
                        </label>
                        <div className="relative">
                            <input
                                id="userId"
                                type="text"
                                placeholder={searchByUsername ? "Discord username" : "Discord user ID"}
                                value={userId}
                                onChange={handleUserInputChange}
                                className="h-11 w-full rounded-lg border border-slate-300 bg-slate-50 px-4 pr-28 text-slate-900 placeholder-slate-400 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/20"
                                onKeyDown={handleKeyPress}
                            />
                            <div className="absolute right-1.5 top-1.5 grid h-8 grid-cols-2 rounded-md border border-slate-200 bg-slate-100 p-0.5 text-xs font-bold dark:border-slate-600 dark:bg-slate-800">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchByUsername(false);
                                        setUserId("");
                                    }}
                                    className={`rounded-md px-2 py-1.5 transition ${!searchByUsername ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-800 dark:text-indigo-400" : "text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"}`}
                                >
                                    ID
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchByUsername(true);
                                        setUserId("");
                                    }}
                                    className={`rounded-md px-2 py-1.5 transition ${searchByUsername ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-800 dark:text-indigo-400" : "text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"}`}
                                >
                                    Name
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex w-full flex-col gap-3 sm:w-44 sm:flex-row xl:w-full xl:flex-col">
                        <div className="w-full space-y-2">
                            <label htmlFor="search-date" className="block text-sm font-bold text-slate-700 dark:text-slate-200">
                                Date
                            </label>
                            <div className="relative">
                                <DatePicker
                                    id="search-date"
                                    selected={date}
                                    onChange={(newDate) => setDate(newDate)}
                                    disabled={!specifyDay}
                                    wrapperClassName="w-full"
                                    className={`h-11 w-full rounded-lg border px-3 pr-20 text-sm transition ${
                                        specifyDay
                                            ? "border-slate-300 bg-slate-50 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                                            : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-600"
                                    } focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/20`}
                                />
                                <label className="absolute right-1.5 top-1.5 flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 text-xs font-bold text-slate-600 shadow-sm transition hover:text-indigo-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-indigo-400">
                                    <input
                                        type="checkbox"
                                        checked={specifyDay}
                                        onChange={(event) => setSpecifyDay(event.target.checked)}
                                        className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-500 focus:ring-indigo-500 dark:border-slate-500 dark:bg-slate-800"
                                    />
                                    On
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
